'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  Oil,
  OiltDto,
  PetrohqApiResponseOilResponse,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

/** GET master/oil 의 유종 행 - 주유소별(pos.store_oil). 생성 타입(Oil)에는 storeCd 가 없어 여기서 더한다 */
type StoreOilRow = Oil & { storeCd?: string };

export function OilManagement() {
  const { toast } = useToast();
  const [oilData, setOilData] = useState<StoreOilRow[]>([]);
  const [oiltData, setOiltData] = useState<OiltDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded1, setIsExpanded1] = useState(false);
  const [isExpanded2, setIsExpanded2] = useState(false);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores, organizations } = useOrganization();

  // 같은 유종코드가 주유소마다 다른 상품일 수 있어 주유소 이름을 붙여 보여준다
  const oilRows = useMemo(
    () =>
      oilData.map(row => ({
        ...row,
        storeName: organizations.find(org => org.orgCd === row.storeCd)?.orgName ?? row.storeCd,
      })),
    [oilData, organizations]
  );

  const fetchOilData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/oil', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as PetrohqApiResponseOilResponse;

      if (data.result) {
        setOilData(data.result.oil || []);
        setOiltData(data.result.oilt || []);
      }
    } catch {
      toast({
        title: '조회 실패',
        description: '유종 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchOilData();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchOilData();
  };

  return (
    <div className='flex flex-col'>
      {/* 일반유 그리드 */}
      <ExpandableCard
        title='일반유'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('oil')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        }
        onExpandChange={setIsExpanded1}
      >
        <div
          className={`border rounded-lg ${isExpanded1 ? 'h-full' : 'h-[400px]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={oilRows}
            >
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilKind'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'buyCost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sellCost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'oilCode4'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'selfCost'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'sellCost2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sellCost3'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sellCost4'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'barCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataColumn fieldName={'storeName'} header={{ text: '매장' }} width={120} />
              <RGDataColumn
                fieldName={'oilCode'}
                header={{ text: '유종코드' }}
                width={60}
              />
              <RGDataColumn fieldName={'oilName'} header={{ text: '유종명' }} />
              <RGDataColumn
                fieldName={'oilKind'}
                header={{ text: '구분' }}
                lookupDisplay={true}
                values={['1', '2', '3', '4', '5']}
                labels={['일반유', 'GAS', '유외상품', '세차', '식품']}
              />
              <RGDataColumn
                fieldName={'buyCost'}
                header={{ text: '매입가' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'sellCost'}
                header={{ text: '판매가 01' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'oilCode4'}
                header={{ text: '판매가 04' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'selfCost'}
                header={{ text: '유개공 유종코드' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'sellCost2'}
                header={{ text: '셀프단가' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'sellCost3'}
                header={{ text: '판매가 02' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'sellCost4'}
                header={{ text: '판매가 03' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'barCode'}
                header={{ text: 'barCode' }}
              />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>

      {/* 면세유 그리드 */}
      <ExpandableCard title='면세유' onExpandChange={setIsExpanded2}>
        <div
          className={`border rounded-lg ${isExpanded2 ? 'h-full' : 'h-[400px]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={oiltData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilKind'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'buyCost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sellCost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sellCost2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '주유소' }}
              />
              <RGDataColumn
                fieldName={'oilCode'}
                header={{ text: '유종코드' }}
                width={60}
              />
              <RGDataColumn fieldName={'oilName'} header={{ text: '유종명' }} />
              <RGDataColumn
                fieldName={'oilKind'}
                header={{ text: '구분' }}
                lookupDisplay={true}
                values={['1', '2', '3', '4']}
                labels={['일반유', 'GAS', '유외상품', '세차']}
              />
              <RGDataColumn
                fieldName={'buyCost'}
                header={{ text: '대리점단가' }}
                width={70}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'sellCost'}
                header={{ text: '고시가' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'sellCost2'}
                header={{ text: '특소세면세유' }}
                width={80}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
